import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'

import styles from './_styles/card-image.scss?inline'

/**
 * `dap-ds-card-image`
 * @summary A card image is a container for images in a card.
 * @element dap-ds-card-image
 * @title - Card image
 * @group card
 *
 * @slot - The default slot for the image. The slot can accept any element, for example a video. If nothing is added to the slot, the image will be rendered.
 *
 * @csspart base - The main card image container.
 *
 * @cssproperty --dds-card-image-width - The width of the image. Default is `100%`.
 * @cssproperty --dds-card-image-height - The height of the image. Default is `auto`.
 * @cssproperty --dds-card-image-object-fit - How the image fits within its container. Default is `cover`.
 * @cssproperty --dds-card-image-background-position - The background position of the image. Default is `center`.
 * @cssproperty --dds-card-image-background-size - How the background image is sized. Default is `cover`.
 * @cssproperty --dds-card-image-background-repeat - How the background image repeats. Default is `no-repeat`.
 */
@customElement('dap-ds-card-image')
export default class DapDSCardImage extends DdsElement {
  /** The source of the image.*/
  @property() src: string
  /** The alt text of the image.*/
  @property() alt: string
  /** The width of the image.*/
  @property({ type: Number }) width: number
  /** The height of the image.*/
  @property({ type: Number }) height: number

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`<slot
      ><img
        part="base"
        class="card-image"
        style=${`width: ${this.width}px; height: ${this.height}px;`}
        alt=${this.alt}
        src=${this.src}
    /></slot>`
  }
}
